import { useCallback } from "react";
import {
  fileWrapper,
  folderWrapper,
} from "@/src/components/data_room/file_uploaders/NewFilesUploader";
import { payloadContainsZip } from "@/src/components/data_room/file_uploaders/parseDataTransferFiles";
import { useUploadApi } from "@/src/components/data_room/file_uploaders/useUploadContext";
import { ExternalDropTarget } from "./useExternalFileDrop";

/**
 * Shared drop → enqueue / modal / auto-upload wiring for folder + tree views.
 * Uses useUploadApi so progress ticks don't re-render the folder/tree list.
 */
export function useDataRoomExternalUpload() {
  const {
    enqueueFiles,
    setTargetFolderId,
    setTargetFolderName,
    setUploadModalOpen,
    clearCompleted,
    startUploads,
  } = useUploadApi();

  const handleExternalDrop = useCallback(
    (args: {
      files: fileWrapper[];
      folders: folderWrapper[];
      target: ExternalDropTarget;
      /** When true, skip modal if payload is eligible for auto-upload */
      allowAutoUpload?: boolean;
    }) => {
      const { files, folders, target, allowAutoUpload = false } = args;

      clearCompleted();
      enqueueFiles(files, folders);
      setTargetFolderId(target.folderId);
      setTargetFolderName(target.folderName);

      const canAuto =
        allowAutoUpload &&
        Boolean(target.permission?.id) &&
        !payloadContainsZip(files, folders);

      if (canAuto) {
        startUploads({
          dataRoomFolderId: target.folderId,
          targetFolderName: target.folderName,
          files,
          folders,
        });
        return;
      }

      setUploadModalOpen(true);
    },
    [
      clearCompleted,
      enqueueFiles,
      setTargetFolderId,
      setTargetFolderName,
      setUploadModalOpen,
      startUploads,
    ],
  );

  return { handleExternalDrop };
}
